<template>
  <DashboardHeader :title="getClientTitle">
    <transition name="fade" mode="out-in">
      <!-- Client Not Found State -->
      <div v-if="clientNotFound" key="not-found">
        <v-row justify="center">
          <v-col cols="12" sm="8" md="6">
            <v-alert
              type="warning"
              prominent
              border="left"
              class="my-8"
              elevation="2"
            >
              <v-row align="center">
                <v-col class="grow">
                  <h3 class="text-h5 mb-2">No Data Found!</h3>
                  <div>The requested client profile could not be located.</div>
                </v-col>
                <v-col class="shrink">
                  <v-icon size="64" color="warning"
                    >mdi-alert-circle-outline</v-icon
                  >
                </v-col>
              </v-row>
            </v-alert>
          </v-col>
        </v-row>
      </div>

      <!-- Main Content -->
      <div v-else key="main-content">
        <v-container fluid class="pa-4">
          <v-row>
            <!-- Left Sidebar - Client Info -->
            <v-col cols="12" :md="isMobile ? 12 : 3" order="1" order-md="1">
              <transition name="slide-fade" appear>
                <div>
                  <!-- Client Selector -->
                  <v-card class="mb-4" elevation="3" shaped>
                    <v-card-text>
                      <SelectClient
                        @client-selected="handleClientSelected"
                        @new-client-created="addClientToList"
                      />
                    </v-card-text>
                  </v-card>

                  <!-- Client Profile Card -->
                  <v-card
                    v-if="client"
                    elevation="6"
                    shaped
                    class="profile-card"
                  >
                    <v-img
                      :src="getProfileBackground"
                      height="120"
                      gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
                    >
                      <v-row class="fill-height pa-4" align="end">
                        <v-col>
                          <v-chip
                            :color="client.is_company ? 'purple' : 'blue'"
                            dark
                            small
                          >
                            <v-icon left small>
                              {{
                                client.is_company
                                  ? "mdi-office-building"
                                  : "mdi-account"
                              }}
                            </v-icon>
                            {{ client.is_company ? "Company" : "Individual" }}
                          </v-chip>
                        </v-col>
                      </v-row>
                    </v-img>

                    <v-card-text class="text-center pt-0">
                      <v-avatar
                        size="100"
                        class="profile-avatar"
                        :color="getThemeColor"
                      >
                        <input
                          type="file"
                          ref="fileInput"
                          style="display: none"
                          accept="image/*"
                          @change="getSelectedImage"
                        />
                        <v-img
                          v-if="profilePic"
                          :src="profilePic"
                          @click="openImageDialog"
                          style="cursor: pointer"
                        />
                        <span
                          v-else
                          class="white--text text-h3"
                          @click="openImageDialog"
                          style="cursor: pointer"
                        >
                          {{ getInitials(client.name) }}
                        </span>
                      </v-avatar>

                      <h2 class="text-h5 mt-3 mb-1">
                        {{ normalizeNames(client.name) }}
                      </h2>
                      <p class="text-subtitle-2 text--secondary mb-3">
                        {{ getCustomerName }} Profile
                      </p>

                      <v-divider class="my-3" />

                      <!-- Client Details List -->
                      <v-list dense class="transparent">
                        <v-list-item>
                          <v-list-item-icon>
                            <v-icon color="indigo"
                              >mdi-card-account-details</v-icon
                            >
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle
                              >Client Reference</v-list-item-subtitle
                            >
                            <v-list-item-title>{{
                              client.client_reference
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item
                          v-if="client.company_name && client.is_company"
                        >
                          <v-list-item-icon>
                            <v-icon color="purple">mdi-office-building</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle
                              >Company Name</v-list-item-subtitle
                            >
                            <v-list-item-title>{{
                              client.company_name
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item
                          v-if="client.registration_number && client.is_company"
                        >
                          <v-list-item-icon>
                            <v-icon color="purple">mdi-file-document</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle
                              >Registration Number</v-list-item-subtitle
                            >
                            <v-list-item-title>{{
                              client.registration_number
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item v-if="client.address">
                          <v-list-item-icon>
                            <v-icon color="indigo">mdi-map-marker</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>Address</v-list-item-subtitle>
                            <v-list-item-title>{{
                              client.address
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item v-if="client.phone">
                          <v-list-item-icon>
                            <v-icon color="indigo">mdi-phone</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>Phone</v-list-item-subtitle>
                            <v-list-item-title>{{
                              client.phone
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item v-if="client.email">
                          <v-list-item-icon>
                            <v-icon color="indigo">mdi-email</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>Email</v-list-item-subtitle>
                            <v-list-item-title>{{
                              client.email
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item
                          v-if="!client.is_company && client.id_number"
                        >
                          <v-list-item-icon>
                            <v-icon color="indigo">mdi-account</v-icon>
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle
                              >ID Number</v-list-item-subtitle
                            >
                            <v-list-item-title>{{
                              client.id_number
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>

                        <v-list-item v-if="!client.is_company && client.gender">
                          <v-list-item-icon>
                            <v-icon color="indigo"
                              >mdi-gender-male-female</v-icon
                            >
                          </v-list-item-icon>
                          <v-list-item-content>
                            <v-list-item-subtitle>Gender</v-list-item-subtitle>
                            <v-list-item-title>{{
                              client.gender
                            }}</v-list-item-title>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>

                      <v-card-text
                        v-if="client.notes"
                        class="text-left text-caption text--secondary"
                      >
                        <v-icon small left>mdi-note-text</v-icon>
                        {{ client.notes }}
                      </v-card-text>
                    </v-card-text>
                  </v-card>
                </div>
              </transition>
            </v-col>

            <!-- Main Content Area -->
            <v-col cols="12" :md="isMobile ? 12 : 9" order="2" order-md="2">
              <transition name="slide-fade" appear>
                <v-card elevation="4" shaped v-if="client">
                  <v-tabs
                    v-model="tab"
                    :background-color="getThemeColor"
                    :color="getSecondaryColor"
                    centered
                    icons-and-text
                    dark
                    show-arrows
                  >
                    <v-tab href="#tab-overview">
                      <span class="hidden-sm-and-down">Overview</span>
                      <v-icon>mdi-view-dashboard</v-icon>
                    </v-tab>

                    <v-tab href="#tab-files">
                      <span class="hidden-sm-and-down">{{
                        getFilesTabLabel
                      }}</span>
                      <v-icon>{{ getFilesTabIcon }}</v-icon>
                    </v-tab>

                    <v-tab href="#tab-invoices">
                      <span class="hidden-sm-and-down">Invoices</span>
                      <v-icon>mdi-receipt</v-icon>
                    </v-tab>

                    <v-tab href="#tab-appointments">
                      <span class="hidden-sm-and-down">Appointments</span>
                      <v-icon>mdi-calendar-clock</v-icon>
                    </v-tab>

                    <v-tab href="#tab-profile">
                      <span class="hidden-sm-and-down">Details</span>
                      <v-icon>mdi-account-box</v-icon>
                    </v-tab>

                    <v-tab href="#tab-reports">
                      <span class="hidden-sm-and-down">Reports</span>
                      <v-icon>mdi-file-document</v-icon>
                    </v-tab>
                  </v-tabs>

                  <v-tabs-items v-model="tab">
                    <!-- Overview Tab -->
                    <v-tab-item value="tab-overview">
                      <v-card flat>
                        <v-card-text>
                          <!-- Financial Summary Cards -->
                          <v-row>
                            <v-col
                              v-for="(info, i) in financeTotal"
                              :key="i"
                              cols="12"
                              sm="6"
                              md="4"
                              lg="2"
                            >
                              <transition
                                name="scale"
                                appear
                                :style="{ transitionDelay: i * 50 + 'ms' }"
                              >
                                <v-card
                                  :color="colors[i]"
                                  dark
                                  elevation="3"
                                  hover
                                  class="finance-card"
                                >
                                  <v-card-text class="text-center">
                                    <div class="text-h6 font-weight-light mb-2">
                                      {{ info.description }}
                                    </div>
                                    <div class="text-h5 font-weight-bold">
                                      <span v-if="i === 0 && isAdmin">
                                        {{ getCurrency }}
                                        <input
                                          type="number"
                                          v-model="info.amount"
                                          @blur="updateOpeningBalance"
                                          class="opening-balance-input"
                                          style="
                                            width: 100px;
                                            background: transparent;
                                            border: none;
                                            border-bottom: 2px solid white;
                                            color: white;
                                            text-align: center;
                                            font-size: 1.25rem;
                                            font-weight: bold;
                                          "
                                        />
                                      </span>
                                      <span v-else>
                                        {{ getCurrency }}{{ info.amount }}
                                      </span>
                                    </div>
                                  </v-card-text>
                                </v-card>
                              </transition>
                            </v-col>
                          </v-row>

                          <v-divider class="my-6" />

                          <!-- Quick Actions Row -->
                          <v-row class="mb-4">
                            <v-col cols="12">
                              <h3 class="text-h6 mb-3">Quick Actions</h3>
                              <v-chip-group>
                                <v-chip
                                  v-if="isAdmin"
                                  :color="getThemeColor"
                                  dark
                                  @click="gotoCreateInvoicesAndQuotes"
                                >
                                  <v-icon left>mdi-plus</v-icon>
                                  New Invoice
                                </v-chip>
                                <v-chip
                                  v-if="isAdmin"
                                  color="success"
                                  dark
                                  @click="openAppointmentDialog"
                                >
                                  <v-icon left>mdi-calendar-plus</v-icon>
                                  Book Appointment
                                </v-chip>
                                <v-chip
                                  v-if="isAdmin"
                                  color="purple"
                                  dark
                                  @click="openPaymentMethodDialog"
                                >
                                  <v-icon left>mdi-credit-card</v-icon>
                                  Payment Methods
                                </v-chip>
                                <v-chip
                                  color="info"
                                  dark
                                  @click="generateClientReport"
                                >
                                  <v-icon left>mdi-file-pdf</v-icon>
                                  Generate Report
                                </v-chip>
                              </v-chip-group>
                            </v-col>
                          </v-row>

                          <!-- Recent Activity Section -->
                          <v-row>
                            <v-col cols="12" md="6">
                              <v-card outlined>
                                <v-card-title class="text-subtitle-1">
                                  <v-icon left color="primary"
                                    >mdi-history</v-icon
                                  >
                                  Recent Invoices
                                </v-card-title>
                                <v-divider />
                                <v-list dense>
                                  <template v-if="clientInvoices.length > 0">
                                    <v-list-item
                                      v-for="(
                                        invoice, idx
                                      ) in clientInvoices.slice(0, 5)"
                                      :key="idx"
                                      @click="viewInvoice(invoice)"
                                    >
                                      <v-list-item-content>
                                        <v-list-item-title>{{
                                          invoice.description
                                        }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                          {{ getCurrency
                                          }}{{ invoice.calculation.total }} -
                                          {{ invoice.due_date }}
                                        </v-list-item-subtitle>
                                      </v-list-item-content>
                                      <v-list-item-action>
                                        <v-chip
                                          x-small
                                          :color="
                                            getInvoiceStatusColor(invoice)
                                          "
                                          dark
                                        >
                                          {{ getInvoiceStatus(invoice) }}
                                        </v-chip>
                                      </v-list-item-action>
                                    </v-list-item>
                                  </template>
                                  <v-list-item v-else>
                                    <v-list-item-content>
                                      <v-list-item-subtitle class="text-center">
                                        No invoices yet
                                      </v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </v-card>
                            </v-col>

                            <v-col cols="12" md="6">
                              <v-card outlined>
                                <v-card-title class="text-subtitle-1">
                                  <v-icon left color="success"
                                    >mdi-calendar</v-icon
                                  >
                                  Upcoming Appointments
                                </v-card-title>
                                <v-divider />
                                <v-list dense>
                                  <template
                                    v-if="clientAppointments.length > 0"
                                  >
                                    <v-list-item
                                      v-for="(
                                        appointment, idx
                                      ) in clientAppointments.slice(0, 5)"
                                      :key="idx"
                                    >
                                      <v-list-item-content>
                                        <v-list-item-title>{{
                                          appointment.title
                                        }}</v-list-item-title>
                                        <v-list-item-subtitle>
                                          {{ formatDate(appointment.date) }} at
                                          {{ appointment.time }}
                                        </v-list-item-subtitle>
                                      </v-list-item-content>
                                      <v-list-item-action>
                                        <v-chip
                                          x-small
                                          :color="
                                            getAppointmentStatusColor(
                                              appointment
                                            )
                                          "
                                          dark
                                        >
                                          {{ appointment.status }}
                                        </v-chip>
                                      </v-list-item-action>
                                    </v-list-item>
                                  </template>
                                  <v-list-item v-else>
                                    <v-list-item-content>
                                      <v-list-item-subtitle class="text-center">
                                        No upcoming appointments
                                      </v-list-item-subtitle>
                                    </v-list-item-content>
                                  </v-list-item>
                                </v-list>
                              </v-card>
                            </v-col>
                          </v-row>
                        </v-card-text>
                      </v-card>
                    </v-tab-item>

                    <!-- Files Tab (Industry-Specific) -->
                    <v-tab-item value="tab-files">
                      <v-card flat>
                        <v-card-text>
                          <!-- Legal Practice - Case Files -->
                          <div
                            v-if="getUserCompany.industry === 'Legal Practice'"
                          >
                            <LegalCaseFiles
                              :client="client"
                              :cases="clientCases"
                              @add-case="openAddCaseDialog"
                              @edit-case="editCase"
                              @delete-case="deleteCase"
                              @view-case="goToManageCases"
                            />
                          </div>

                          <!-- Private Health - Medical Files -->
                          <div
                            v-else-if="
                              getUserCompany.industry === 'Private Health'
                            "
                          >
                            <MedicalFiles
                              :client="client"
                              :medicalFiles="clientMedicalFiles"
                              @add-file="openAddMedicalFileDialog"
                              @edit-file="editMedicalFile"
                              @delete-file="deleteMedicalFile"
                              @view-file="viewMedicalFile"
                            />
                          </div>

                          <!-- Mechanics - Service History -->
                          <div
                            v-else-if="getUserCompany.industry === 'Mechanics'"
                          >
                            <VehicleServiceFiles
                              :client="client"
                              :vehicles="clientVehicles"
                              @add-vehicle="openAddVehicleDialog"
                              @edit-vehicle="editVehicle"
                              @delete-vehicle="deleteVehicle"
                              @view-service="viewServiceHistory"
                            />
                          </div>

                          <!-- General - Document Files -->
                          <div v-else>
                            <GeneralFiles
                              :client="client"
                              :files="clientFiles"
                              @add-file="openAddFileDialog"
                              @edit-file="editFile"
                              @delete-file="deleteFile"
                              @view-file="viewFile"
                            />
                          </div>
                        </v-card-text>
                      </v-card>
                    </v-tab-item>

                    <!-- Invoices Tab -->
                    <v-tab-item value="tab-invoices">
                      <v-card flat>
                        <v-card-text>
                          <InvoiceTable
                            v-if="client"
                            :invoices="clientInvoices"
                            @refresh-needed="refreshClientDetails"
                            @view-invoice="viewInvoice"
                            @delete-invoice="deleteInvoice"
                          />
                        </v-card-text>
                      </v-card>
                    </v-tab-item>

                    <!-- Appointments Tab -->
                    <v-tab-item value="tab-appointments">
                      <v-card flat>
                        <v-card-text>
                          <AppointmentsManager
                            :client="client"
                            :appointments="clientAppointments"
                            @add-appointment="openAppointmentDialog"
                            @edit-appointment="editAppointment"
                            @delete-appointment="deleteAppointment"
                            @refresh="loadAppointments"
                          />
                        </v-card-text>
                      </v-card>
                    </v-tab-item>

                    <!-- Profile Details Tab -->
                    <v-tab-item value="tab-profile">
                      <v-card flat>
                        <v-card-text>
                          <ClientDetail
                            :client="client"
                            :editable="isAdmin"
                            @update-client="updateClient"
                          />
                        </v-card-text>
                      </v-card>
                    </v-tab-item>

                    <!-- Reports Tab -->
                    <v-tab-item value="tab-reports">
                      <v-card flat>
                        <v-card-text>
                          <ReportsSection
                            :client="client"
                            @generate-statement="generateStatement"
                            @generate-report="generateClientReport"
                            @generate-receipt="generateReceipt"
                          />
                        </v-card-text>
                      </v-card>
                    </v-tab-item>
                  </v-tabs-items>
                </v-card>
              </transition>
            </v-col>
          </v-row>
        </v-container>

        <!-- Floating Action Buttons -->
        <v-speed-dial
          v-if="client && isAdmin"
          v-model="fab"
          bottom
          right
          fixed
          direction="top"
          transition="slide-y-reverse-transition"
        >
          <template v-slot:activator>
            <v-btn v-model="fab" :color="getThemeColor" dark fab large>
              <v-icon v-if="fab">mdi-close</v-icon>
              <v-icon v-else>mdi-menu</v-icon>
            </v-btn>
          </template>

          <v-btn fab dark small color="green" @click="openSMSDialog">
            <v-icon>mdi-message-text</v-icon>
          </v-btn>

          <v-btn fab dark small color="indigo" @click="openEditDialog">
            <v-icon>mdi-pencil</v-icon>
          </v-btn>

          <v-btn fab dark small color="purple" @click="openNotesDialog">
            <v-icon>mdi-note-text</v-icon>
          </v-btn>
        </v-speed-dial>
      </div>
    </transition>

    <!-- Dialogs -->
    <PaymentMethodDialog
      v-model="paymentMethodDialog"
      :client="client"
      @save="savePaymentMethods"
    />

    <AppointmentDialog
      v-model="appointmentDialog"
      :client="client"
      :appointment="selectedAppointment"
      @save="saveAppointment"
    />

    <AddCaseDialog
      v-if="getUserCompany.industry === 'Legal Practice'"
      v-model="addCaseDialog"
      :client="client"
      @save="saveCase"
    />

    <AddMedicalFileDialog
      v-if="getUserCompany.industry === 'Private Health'"
      v-model="addMedicalFileDialog"
      :client="client"
      @save="saveMedicalFile"
    />

    <AddVehicleDialog
      v-if="getUserCompany.industry === 'Mechanics'"
      v-model="addVehicleDialog"
      :client="client"
      @save="saveVehicle"
    />

    <SMSDialog v-model="smsDialog" :client="client" @send="sendSMS" />

    <NotesDialog
      v-model="notesDialog"
      :client="client"
      :notes="clientNotes"
      @save="saveNote"
      @delete="deleteNote"
    />

    <!-- Snackbar for notifications -->
    <v-snackbar
      v-model="snackbar.show"
      :color="snackbar.color"
      :timeout="3000"
      top
      right
    >
      {{ snackbar.text }}
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="snackbar.show = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <Loader />
  </DashboardHeader>
</template>

<script>
import DashboardHeader from "@/components/DashboardHeader.vue";
import Loader from "@/components/Loader.vue";
import SelectClient from "@/components/Clients/SelectClient.vue";
import ClientDetail from "@/components/Clients/ClientDetail.vue";
import InvoiceTable from "@/components/InvoicesAndQuotes/InvoiceTable.vue";
import axios from "@/plugins/axios";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// Industry-specific components (to be created)
import LegalCaseFiles from "./LegalCaseFiles.vue";
import MedicalFiles from "./MedicalFiles.vue";
import VehicleServiceFiles from "./VehicleServiceFiles.vue";
import GeneralFiles from "./GeneralFiles.vue";
import AppointmentsManager from "./AppointmentsManager.vue";
import ReportsSection from "./ReportsSection.vue";

// Dialog components (to be created)
import PaymentMethodDialog from "./PaymentMethodDialog.vue";
import AppointmentDialog from "./AppointmentDialog.vue";
import AddCaseDialog from "./AddCaseDialog.vue";
import AddMedicalFileDialog from "./AddMedicalFileDialog.vue";
import AddVehicleDialog from "./AddVehicleDialog.vue";
import SMSDialog from "./SMSDialog.vue";
import NotesDialog from "./NotesDialog.vue";

export default {
  name: "ClientProfile",

  components: {
    DashboardHeader,
    Loader,
    SelectClient,
    ClientDetail,
    InvoiceTable,
    LegalCaseFiles,
    MedicalFiles,
    VehicleServiceFiles,
    GeneralFiles,
    AppointmentsManager,
    ReportsSection,
    PaymentMethodDialog,
    AppointmentDialog,
    AddCaseDialog,
    AddMedicalFileDialog,
    AddVehicleDialog,
    SMSDialog,
    NotesDialog,
  },

  data() {
    return {
      tab: "tab-overview",
      client: null,
      clientNotFound: false,
      profilePic: null,
      selectedClientReference: null,

      // Financial data
      financeTotal: [
        { description: "Opening", amount: 0 },
        { description: "Quotes", amount: 0 },
        { description: "Invoices", amount: 0 },
        { description: "Paid", amount: 0 },
        { description: "Outstanding", amount: 0 },
      ],
      colors: ["orange", "info", "success", "green darken-2", "red darken-1"],

      // Client data
      clientInvoices: [],
      clientQuotes: [],
      clientAppointments: [],
      clientNotes: [],

      // Industry-specific data
      clientCases: [], // Legal
      clientMedicalFiles: [], // Health
      clientVehicles: [], // Mechanics
      clientFiles: [], // General

      // Dialog states
      paymentMethodDialog: false,
      appointmentDialog: false,
      addCaseDialog: false,
      addMedicalFileDialog: false,
      addVehicleDialog: false,
      smsDialog: false,
      notesDialog: false,

      // Selected items for editing
      selectedAppointment: null,
      selectedCase: null,
      selectedMedicalFile: null,
      selectedVehicle: null,
      selectedPaymentMethod: null,

      // Form data
      appointmentForm: {
        date: new Date().toISOString().substr(0, 10),
        time: "",
        type: "",
        notes: "",
        status: "scheduled",
      },

      paymentMethodForm: {
        type: "cash",
        insurance_provider: "",
        insurance_policy_number: "",
        insurance_expiry: "",
        payment_terms: "",
      },

      medicalFileForm: {
        visit_date: new Date().toISOString().substr(0, 10),
        chief_complaint: "",
        diagnosis: "",
        treatment: "",
        prescription: "",
        notes: "",
        next_appointment: "",
      },

      vehicleForm: {
        make: "",
        model: "",
        year: "",
        registration: "",
        vin: "",
        mileage: "",
      },

      serviceHistoryForm: {
        date: new Date().toISOString().substr(0, 10),
        service_type: "",
        description: "",
        cost: 0,
        mileage: "",
        technician: "",
      },

      // PDF generation
      generatingPDF: false,
      pdfType: "", // 'statement', 'receipt', 'report'

      // Snackbar
      snackbar: {
        show: false,
        text: "",
        color: "success",
        timeout: 3000,
      },

      // Appointments
      appointments: [],
      appointmentTypes: [
        "Consultation",
        "Follow-up",
        "Procedure",
        "Check-up",
        "Emergency",
      ],

      // Medical files
      medicalFiles: [],
      medicalHistory: [],

      // Vehicles
      vehicles: [],
      selectedVehicleId: null,
      serviceHistory: [],

      // Notes
      noteForm: {
        title: "",
        body: "",
        date: new Date().toISOString().substr(0, 10),
      },

      // Validation rules
      rules: {
        required: (v) => !!v || "This field is required",
        email: (v) => !v || /.+@.+\..+/.test(v) || "Invalid email address",
        phone: (v) => !v || /^[0-9+\-\s()]+$/.test(v) || "Invalid phone number",
      },
    };
  },

  computed: {
    // ... existing computed properties ...
    isMobile() {
      return this.$store.getters.isMobile;
    },
    getUserCompany() {
      return this.$store.getters.getUserCompany;
    },
    getClientTitle() {
      if (!this.client) return "Client Profile";
      return `${this.normalizeNames(this.client.name)} - ${
        this.getCustomerName
      } Profile`;
    },

    clientTypeLabel() {
      if (!this.client) return "";
      return this.client.entity_type === "company" ? "Company" : "Individual";
    },

    clientTypeIcon() {
      if (!this.client) return "mdi-account";
      return this.client.entity_type === "company"
        ? "mdi-domain"
        : "mdi-account";
    },

    clientTypeColor() {
      if (!this.client) return "primary";
      return this.client.entity_type === "company" ? "blue darken-2" : "teal";
    },

    defaultProfileImage() {
      if (!this.client) return "";
      return this.client.entity_type === "company"
        ? "/default-company.png"
        : "/default-profile.png";
    },

    industrySpecificLabel() {
      const industry = this.getUserCompany.industry;
      const labels = {
        "Legal Practice": "Case Files",
        "Private Health": "Medical Files",
        Mechanics: "Vehicles",
        General: "Files",
      };
      return labels[industry] || "Files";
    },

    industrySpecificIcon() {
      const industry = this.getUserCompany.industry;
      const icons = {
        "Legal Practice": "mdi-gavel",
        "Private Health": "mdi-medical-bag",
        Mechanics: "mdi-car-wrench",
        General: "mdi-file-document",
      };
      return icons[industry] || "mdi-file";
    },

    showCaseFiles() {
      return this.getUserCompany.industry === "Legal Practice";
    },

    showMedicalFiles() {
      return this.getUserCompany.industry === "Private Health";
    },

    showVehicles() {
      return this.getUserCompany.industry === "Mechanics";
    },

    financeSummaryFormatted() {
      return this.financeTotal.map((item) => ({
        ...item,
        amountFormatted: `${this.getCurrency} ${this.formatNumber(
          item.amount
        )}`,
      }));
    },

    upcomingAppointments() {
      const now = new Date();
      return this.appointments
        .filter((apt) => {
          const aptDate = new Date(apt.date);
          return aptDate >= now && apt.status !== "cancelled";
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));
    },

    pastAppointments() {
      const now = new Date();
      return this.appointments
        .filter((apt) => {
          const aptDate = new Date(apt.date);
          return aptDate < now;
        })
        .sort((a, b) => new Date(b.date) - new Date(a.date));
    },
  },

  watch: {
    selectedClientReference(newVal) {
      if (newVal) {
        this.getClientMethod(newVal);
      }
    },
  },

  methods: {
    // Client selection handlers
    getClientProfile(client) {
      console.log(client);
    },
    addClientToList(client) {
      console.log(client);
    },
    savePaymentMethods() {
      //some
    },
    closeDialog() {
      //some
    },
    handleClientSelected(clientObj) {
      this.selectedClientReference = clientObj.id_number;
      this.getClientMethod(clientObj.id_number);
    },

    handleNewClient(client) {
      this.selectedClientReference = client.id_number;
      this.getClientMethod(client.id_number);
    },

    // Main client data fetching
    async getClientMethod(clientReference) {
      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/get-client-profile",
          { clientReference },
          this.getAccessToken
        );

        if (response === "not found") {
          this.clientNotFound = true;
          this.$store.commit("setLoader", false);
          return;
        }

        this.clientNotFound = false;
        this.client = response.client;
        this.$store.commit("setCurrentClient", response.client);
        this.$store.commit("setCurrentClientProfile", response);

        // Load industry-specific data
        if (this.showCaseFiles) {
          this.clientCases = response.cases || [];
        }

        if (this.showMedicalFiles) {
          this.medicalFiles = response.medical_files || [];
          this.medicalHistory = response.medical_history || [];
        }

        if (this.showVehicles) {
          this.vehicles = response.vehicles || [];
        }

        // Load common data
        this.clientInvoices = response.invoices || [];
        this.clientQuotes = response.quotes || [];
        this.appointments = response.appointments || [];
        this.notes = response.notes || [];

        // Update finance totals
        this.updateFinanceTotals(response);

        // Load profile image
        this.loadProfileImage();

        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error fetching client:", error);
        this.showSnackbar("Error loading client profile", "error");
        this.$store.commit("setLoader", false);
      }
    },

    updateFinanceTotals(response) {
      this.financeTotal[0].amount = response.arrear
        ? response.arrear.amount
        : 0;
      this.financeTotal[1].amount = response.total_quotes_issued || 0;
      this.financeTotal[2].amount = response.total_invoices_due || 0;
      this.financeTotal[3].amount = response.total_invoices_paid || 0;
      this.financeTotal[4].amount =
        Number(response.total_invoices_due || 0) -
        Number(response.total_invoices_paid || 0);
    },

    loadProfileImage() {
      if (!this.client) return;

      const folder =
        this.client.entity_type === "company"
          ? "company_logos"
          : "client_profile_pics";

      this.profilePic = `${this.getApiUrl}/images/${folder}/${this.client.id_number}.jpeg`;
    },

    // Image upload
    triggerImageUpload() {
      this.$refs.fileInput.click();
    },

    async handleImageUpload(event) {
      const file = event.target.files[0];
      if (!file) return;

      const ext = file.type.split("/")[1];
      const imageName = `${this.client.id_number}.${ext}`;

      try {
        const fileReader = new FileReader();
        fileReader.onload = async () => {
          const base64Image = fileReader.result.split(",")[1];
          this.$store.commit("setLoader", true);

          const folder =
            this.client.entity_type === "company"
              ? "company_logos"
              : "client_profile_pics";

          await axios.postDataWithToken(
            "/auth/upload-client-image",
            {
              image64: base64Image,
              name: imageName,
              folder: folder,
              clientId: this.client.id_number,
            },
            this.getAccessToken
          );

          this.loadProfileImage();
          this.showSnackbar("Profile image updated successfully");
          this.$store.commit("setLoader", false);
        };

        fileReader.readAsDataURL(file);
      } catch (error) {
        console.error("Error uploading image:", error);
        this.showSnackbar("Error uploading image", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // Payment Methods
    openPaymentMethodDialog(method = null) {
      if (method) {
        this.selectedPaymentMethod = method;
        this.paymentMethodForm = { ...method };
      } else {
        this.selectedPaymentMethod = null;
        this.paymentMethodForm = {
          type: "cash",
          insurance_provider: "",
          insurance_policy_number: "",
          insurance_expiry: "",
          payment_terms: "",
        };
      }
      this.paymentMethodDialog = true;
    },

    async savePaymentMethod() {
      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/save-payment-method",
          {
            clientId: this.client.id_number,
            paymentMethod: this.paymentMethodForm,
            methodId: this.selectedPaymentMethod
              ? this.selectedPaymentMethod.id
              : null,
          },
          this.getAccessToken
        );

        this.client.payment_methods = response.payment_methods;
        this.showSnackbar("Payment method saved successfully");
        this.paymentMethodDialog = false;
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error saving payment method:", error);
        this.showSnackbar("Error saving payment method", "error");
        this.$store.commit("setLoader", false);
      }
    },

    async deletePaymentMethod(methodId) {
      if (!confirm("Are you sure you want to delete this payment method?"))
        return;

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/delete-payment-method",
          {
            clientId: this.client.id_number,
            methodId: methodId,
          },
          this.getAccessToken
        );

        this.client.payment_methods = response.payment_methods;
        this.showSnackbar("Payment method deleted successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error deleting payment method:", error);
        this.showSnackbar("Error deleting payment method", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // Appointments
    openAppointmentDialog(appointment = null) {
      if (appointment) {
        this.selectedAppointment = appointment;
        this.appointmentForm = { ...appointment };
      } else {
        this.selectedAppointment = null;
        this.appointmentForm = {
          date: new Date().toISOString().substr(0, 10),
          time: "",
          type: "",
          notes: "",
          status: "scheduled",
        };
      }
      this.appointmentDialog = true;
    },

    async saveAppointment() {
      if (!this.appointmentForm.date || !this.appointmentForm.time) {
        this.showSnackbar("Please fill in all required fields", "warning");
        return;
      }

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/save-appointment",
          {
            clientId: this.client.id_number,
            appointment: this.appointmentForm,
            appointmentId: this.selectedAppointment
              ? this.selectedAppointment.id
              : null,
          },
          this.getAccessToken
        );

        this.appointments = response.appointments;
        this.showSnackbar("Appointment saved successfully");
        this.appointmentDialog = false;
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error saving appointment:", error);
        this.showSnackbar("Error saving appointment", "error");
        this.$store.commit("setLoader", false);
      }
    },

    async deleteAppointment(appointmentId) {
      if (!confirm("Are you sure you want to delete this appointment?")) return;

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/delete-appointment",
          {
            clientId: this.client.id_number,
            appointmentId: appointmentId,
          },
          this.getAccessToken
        );

        this.appointments = response.appointments;
        this.showSnackbar("Appointment deleted successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error deleting appointment:", error);
        this.showSnackbar("Error deleting appointment", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // Case Files (Legal Practice)
    openCaseDialog(caseFile = null) {
      this.selectedCase = caseFile;
      this.addCaseDialog = true;
    },

    handleCaseAdded(caseFile) {
      this.clientCases.push(caseFile);
      this.addCaseDialog = false;
      this.showSnackbar("Case file created successfully");
    },

    goToManageCases(caseFile) {
      this.$router.push({
        name: "manage-files",
        params: { caseObj: caseFile },
      });
    },

    async deleteCaseFile(caseId) {
      if (!confirm("Are you sure you want to delete this case file?")) return;

      this.$store.commit("setLoader", true);

      try {
        await axios.postDataWithToken(
          "/auth/delete-case-file",
          {
            clientId: this.client.id_number,
            caseId: caseId,
          },
          this.getAccessToken
        );

        this.clientCases = this.clientCases.filter((c) => c.id !== caseId);
        this.showSnackbar("Case file deleted successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error deleting case file:", error);
        this.showSnackbar("Error deleting case file", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // Medical Files (Private Health)
    openMedicalFileDialog(file = null) {
      if (file) {
        this.selectedMedicalFile = file;
        this.medicalFileForm = { ...file };
      } else {
        this.selectedMedicalFile = null;
        this.medicalFileForm = {
          visit_date: new Date().toISOString().substr(0, 10),
          chief_complaint: "",
          diagnosis: "",
          treatment: "",
          prescription: "",
          notes: "",
          next_appointment: "",
        };
      }
      this.addMedicalFileDialog = true;
    },

    async saveMedicalFile() {
      if (
        !this.medicalFileForm.visit_date ||
        !this.medicalFileForm.chief_complaint
      ) {
        this.showSnackbar("Please fill in all required fields", "warning");
        return;
      }

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/save-medical-file",
          {
            clientId: this.client.id_number,
            medicalFile: this.medicalFileForm,
            fileId: this.selectedMedicalFile
              ? this.selectedMedicalFile.id
              : null,
          },
          this.getAccessToken
        );

        this.medicalFiles = response.medical_files;
        this.medicalHistory = response.medical_history;
        this.showSnackbar("Medical record saved successfully");
        this.addMedicalFileDialog = false;
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error saving medical file:", error);
        this.showSnackbar("Error saving medical record", "error");
        this.$store.commit("setLoader", false);
      }
    },

    async deleteMedicalFile(fileId) {
      if (!confirm("Are you sure you want to delete this medical record?"))
        return;

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/delete-medical-file",
          {
            clientId: this.client.id_number,
            fileId: fileId,
          },
          this.getAccessToken
        );

        this.medicalFiles = response.medical_files;
        this.showSnackbar("Medical record deleted successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error deleting medical file:", error);
        this.showSnackbar("Error deleting medical record", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // Vehicles (Mechanics)
    openVehicleDialog(vehicle = null) {
      if (vehicle) {
        this.selectedVehicle = vehicle;
        this.vehicleForm = { ...vehicle };
      } else {
        this.selectedVehicle = null;
        this.vehicleForm = {
          make: "",
          model: "",
          year: "",
          registration: "",
          vin: "",
          mileage: "",
        };
      }
      this.addVehicleDialog = true;
    },

    async saveVehicle() {
      if (
        !this.vehicleForm.make ||
        !this.vehicleForm.model ||
        !this.vehicleForm.registration
      ) {
        this.showSnackbar("Please fill in all required fields", "warning");
        return;
      }

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/save-vehicle",
          {
            clientId: this.client.id_number,
            vehicle: this.vehicleForm,
            vehicleId: this.selectedVehicle ? this.selectedVehicle.id : null,
          },
          this.getAccessToken
        );

        this.vehicles = response.vehicles;
        this.showSnackbar("Vehicle saved successfully");
        this.addVehicleDialog = false;
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error saving vehicle:", error);
        this.showSnackbar("Error saving vehicle", "error");
        this.$store.commit("setLoader", false);
      }
    },

    async deleteVehicle(vehicleId) {
      if (!confirm("Are you sure you want to delete this vehicle?")) return;

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/delete-vehicle",
          {
            clientId: this.client.id_number,
            vehicleId: vehicleId,
          },
          this.getAccessToken
        );

        this.vehicles = response.vehicles;
        this.showSnackbar("Vehicle deleted successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error deleting vehicle:", error);
        this.showSnackbar("Error deleting vehicle", "error");
        this.$store.commit("setLoader", false);
      }
    },

    async loadServiceHistory(vehicleId) {
      this.selectedVehicleId = vehicleId;
      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/get-service-history",
          {
            vehicleId: vehicleId,
          },
          this.getAccessToken
        );

        this.serviceHistory = response.service_history;
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error loading service history:", error);
        this.showSnackbar("Error loading service history", "error");
        this.$store.commit("setLoader", false);
      }
    },

    async saveServiceHistory() {
      if (
        !this.serviceHistoryForm.service_type ||
        !this.serviceHistoryForm.date
      ) {
        this.showSnackbar("Please fill in all required fields", "warning");
        return;
      }

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/save-service-history",
          {
            vehicleId: this.selectedVehicleId,
            serviceHistory: this.serviceHistoryForm,
          },
          this.getAccessToken
        );

        this.serviceHistory = response.service_history;
        this.showSnackbar("Service record saved successfully");
        this.serviceHistoryForm = {
          date: new Date().toISOString().substr(0, 10),
          service_type: "",
          description: "",
          cost: 0,
          mileage: "",
          technician: "",
        };
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error saving service history:", error);
        this.showSnackbar("Error saving service record", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // Invoices
    viewInvoice(invoice) {
      this.$router.push({
        name: "edit-and-print-invoice",
        params: { selectedInvoice: invoice },
      });
    },

    gotoCreateInvoicesAndQuotes() {
      this.$router.push({
        name: "invoices-and-quotes",
        params: { clientId: this.client.id_number },
      });
    },

    async deleteInvoice(invoiceId) {
      if (!confirm("Are you sure you want to delete this invoice?")) return;

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/delete-invoice",
          {
            invoiceId: invoiceId,
          },
          this.getAccessToken
        );

        this.clientInvoices = this.clientInvoices.filter(
          (inv) => inv.id !== invoiceId
        );
        this.showSnackbar("Invoice deleted successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error deleting invoice:", error);
        this.showSnackbar("Error deleting invoice", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // PDF Generation
    async generatePDF(type) {
      this.generatingPDF = true;
      this.pdfType = type;

      try {
        // Import pdfmake dynamically
        const pdfMake = await import("pdfmake/build/pdfmake");
        const pdfFonts = await import("pdfmake/build/vfs_fonts");
        pdfMake.vfs = pdfFonts.pdfMake.vfs;

        let docDefinition = {};

        switch (type) {
          case "statement":
            docDefinition = this.generateStatementPDF();
            break;
          case "receipt":
            docDefinition = this.generateReceiptPDF();
            break;
          case "report":
            docDefinition = this.generateReportPDF();
            break;
        }

        pdfMake
          .createPdf(docDefinition)
          .download(`${type}-${this.client.name}-${new Date().getTime()}.pdf`);
        this.showSnackbar(
          `${
            type.charAt(0).toUpperCase() + type.slice(1)
          } generated successfully`
        );
      } catch (error) {
        console.error("Error generating PDF:", error);
        this.showSnackbar("Error generating PDF", "error");
      } finally {
        this.generatingPDF = false;
      }
    },

    generateStatementPDF() {
      const invoiceRows = this.clientInvoices.map((inv) => [
        inv.invoice_number || "",
        inv.date || "",
        inv.description || "",
        `${this.getCurrency} ${this.formatNumber(inv.calculation.total)}`,
      ]);

      return {
        content: [
          { text: "CLIENT STATEMENT", style: "header" },
          { text: this.getUserCompany.name, style: "subheader" },
          { text: "\n" },
          { text: "CLIENT INFORMATION", style: "sectionHeader" },
          {
            columns: [
              {
                width: "50%",
                text: [
                  { text: "Name: ", bold: true },
                  this.client.name + "\n",
                  { text: "Reference: ", bold: true },
                  this.client.client_reference + "\n",
                  { text: "Type: ", bold: true },
                  this.clientTypeLabel,
                ],
              },
              {
                width: "50%",
                text: [
                  { text: "Address: ", bold: true },
                  (this.client.address || "N/A") + "\n",
                  { text: "Phone: ", bold: true },
                  (this.client.phone || "N/A") + "\n",
                  { text: "Email: ", bold: true },
                  this.client.email || "N/A",
                ],
              },
            ],
          },
          { text: "\n" },
          { text: "FINANCIAL SUMMARY", style: "sectionHeader" },
          {
            table: {
              widths: ["*", "auto"],
              body: [
                [
                  { text: "Opening Balance", bold: true },
                  `${this.getCurrency} ${this.formatNumber(
                    this.financeTotal[0].amount
                  )}`,
                ],
                [
                  { text: "Total Quotes", bold: true },
                  `${this.getCurrency} ${this.formatNumber(
                    this.financeTotal[1].amount
                  )}`,
                ],
                [
                  { text: "Total Invoices", bold: true },
                  `${this.getCurrency} ${this.formatNumber(
                    this.financeTotal[2].amount
                  )}`,
                ],
                [
                  { text: "Total Paid", bold: true },
                  `${this.getCurrency} ${this.formatNumber(
                    this.financeTotal[3].amount
                  )}`,
                ],
                [
                  {
                    text: "Outstanding Balance",
                    bold: true,
                    fillColor: "#eeeeee",
                  },
                  {
                    text: `${this.getCurrency} ${this.formatNumber(
                      this.financeTotal[4].amount
                    )}`,
                    bold: true,
                    fillColor: "#eeeeee",
                  },
                ],
              ],
            },
          },
          { text: "\n" },
          { text: "INVOICE HISTORY", style: "sectionHeader" },
          {
            table: {
              headerRows: 1,
              widths: ["auto", "auto", "*", "auto"],
              body: [
                [
                  { text: "Invoice #", bold: true },
                  { text: "Date", bold: true },
                  { text: "Description", bold: true },
                  { text: "Amount", bold: true },
                ],
                ...invoiceRows,
              ],
            },
          },
          { text: "\n" },
          {
            text: `Generated on ${new Date().toLocaleDateString()}`,
            style: "footer",
          },
        ],
        styles: {
          header: {
            fontSize: 22,
            bold: true,
            alignment: "center",
            margin: [0, 0, 0, 10],
          },
          subheader: {
            fontSize: 14,
            alignment: "center",
            margin: [0, 0, 0, 5],
          },
          sectionHeader: {
            fontSize: 14,
            bold: true,
            margin: [0, 10, 0, 5],
          },
          footer: {
            fontSize: 10,
            italics: true,
            alignment: "right",
          },
        },
      };
    },

    generateReceiptPDF() {
      const lastInvoice =
        this.clientInvoices.length > 0 ? this.clientInvoices[0] : null;

      return {
        content: [
          { text: "PAYMENT RECEIPT", style: "header" },
          { text: this.getUserCompany.name, style: "subheader" },
          { text: "\n" },
          {
            columns: [
              {
                width: "50%",
                text: [
                  { text: "Receipt Date: ", bold: true },
                  new Date().toLocaleDateString() + "\n",
                  { text: "Receipt #: ", bold: true },
                  "RCP-" + new Date().getTime(),
                ],
              },
              {
                width: "50%",
                text: [
                  { text: "Client: ", bold: true },
                  this.client.name + "\n",
                  { text: "Reference: ", bold: true },
                  this.client.client_reference,
                ],
              },
            ],
          },
          { text: "\n" },
          { text: "PAYMENT DETAILS", style: "sectionHeader" },
          {
            table: {
              widths: ["*", "auto"],
              body: [
                [
                  "Description",
                  lastInvoice ? lastInvoice.description : "Payment",
                ],
                [
                  "Amount Paid",
                  this.getCurrency +
                    " " +
                    this.formatNumber(
                      lastInvoice ? lastInvoice.calculation.total : 0
                    ),
                ],
                [
                  "Payment Method",
                  this.client.payment_methods &&
                  this.client.payment_methods.length > 0
                    ? this.client.payment_methods[0].type
                    : "Cash",
                ],
              ],
            },
          },
          { text: "\n" },
          {
            text: "Thank you for your payment!",
            alignment: "center",
            italics: true,
          },
        ],
        styles: {
          header: {
            fontSize: 22,
            bold: true,
            alignment: "center",
            margin: [0, 0, 0, 10],
          },
          subheader: {
            fontSize: 14,
            alignment: "center",
            margin: [0, 0, 0, 5],
          },
          sectionHeader: {
            fontSize: 14,
            bold: true,
            margin: [0, 10, 0, 5],
          },
        },
      };
    },
    generateReportPDF() {
      let industrySpecificContent = [];

      if (this.showCaseFiles) {
        industrySpecificContent = [
          { text: "CASE FILES", style: "sectionHeader" },
          {
            ul: this.clientCases.map(
              (c) => `${c.title} - ${c.status ? c.status.name : "Active"}`
            ),
          },
        ];
      } else if (this.showMedicalFiles) {
        industrySpecificContent = [
          { text: "MEDICAL HISTORY", style: "sectionHeader" },
          {
            ul: this.medicalFiles.map(
              (f) => `${f.visit_date}: ${f.chief_complaint}`
            ),
          },
        ];
      } else if (this.showVehicles) {
        industrySpecificContent = [
          { text: "VEHICLES", style: "sectionHeader" },
          {
            ul: this.vehicles.map(
              (v) => `${v.year} ${v.make} ${v.model} - ${v.registration}`
            ),
          },
        ];
      }

      return {
        content: [
          { text: "CLIENT REPORT", style: "header" },
          { text: this.getUserCompany.name, style: "subheader" },
          { text: "\n" },
          { text: "CLIENT OVERVIEW", style: "sectionHeader" },
          {
            text: [
              { text: "Name: ", bold: true },
              this.client.name + "\n",
              { text: "Type: ", bold: true },
              this.clientTypeLabel + "\n",
              { text: "Reference: ", bold: true },
              this.client.client_reference + "\n",
              { text: "Phone: ", bold: true },
              (this.client.phone || "N/A") + "\n",
              { text: "Email: ", bold: true },
              this.client.email || "N/A",
            ],
          },
          { text: "\n" },
          ...industrySpecificContent,
          { text: "\n" },
          { text: "APPOINTMENTS", style: "sectionHeader" },
          {
            text:
              this.appointments.length > 0
                ? `Total Appointments: ${this.appointments.length}`
                : "No appointments scheduled",
          },
          { text: "\n" },
          {
            text: `Report generated on ${new Date().toLocaleString()}`,
            style: "footer",
          },
        ],
        styles: {
          header: {
            fontSize: 22,
            bold: true,
            alignment: "center",
            margin: [0, 0, 0, 10],
          },
          subheader: {
            fontSize: 14,
            alignment: "center",
            margin: [0, 0, 0, 5],
          },
          sectionHeader: {
            fontSize: 14,
            bold: true,
            margin: [0, 10, 0, 5],
          },
          footer: {
            fontSize: 10,
            italics: true,
            alignment: "right",
          },
        },
      };
    },

    // Notes
    async saveNote() {
      if (!this.noteForm.title || !this.noteForm.body) {
        this.showSnackbar("Please fill in note title and body", "warning");
        return;
      }

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/save-note",
          {
            clientId: this.client.id_number,
            note: this.noteForm,
          },
          this.getAccessToken
        );

        this.notes = response.notes;
        this.showSnackbar("Note saved successfully");
        this.notesDialog = false;
        this.noteForm = {
          title: "",
          body: "",
          date: new Date().toISOString().substr(0, 10),
        };
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error saving note:", error);
        this.showSnackbar("Error saving note", "error");
        this.$store.commit("setLoader", false);
      }
    },

    async deleteNote(noteId) {
      if (!confirm("Are you sure you want to delete this note?")) return;

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/delete-note",
          {
            clientId: this.client.id_number,
            noteId: noteId,
          },
          this.getAccessToken
        );

        this.notes = response.notes;
        this.showSnackbar("Note deleted successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error deleting note:", error);
        this.showSnackbar("Error deleting note", "error");
        this.$store.commit("setLoader", false);
      }
    },

    openNotesDialog(note = null) {
      if (note) {
        this.noteForm = { ...note };
      } else {
        this.noteForm = {
          title: "",
          body: "",
          date: new Date().toISOString().substr(0, 10),
        };
      }
      this.notesDialog = true;
    },

    // SMS
    openSMSDialog() {
      this.smsDialog = true;
    },

    async sendSMS(message) {
      if (!message || !message.trim()) {
        this.showSnackbar("Please enter a message", "warning");
        return;
      }

      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/send-sms",
          {
            clientId: this.client.id_number,
            phone: this.client.phone,
            message: message,
          },
          this.getAccessToken
        );

        this.showSnackbar("SMS sent successfully");
        this.smsDialog = false;
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error sending SMS:", error);
        this.showSnackbar("Error sending SMS", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // Update client information
    async updateClientInfo(updatedData) {
      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/update-client-info",
          {
            clientId: this.client.id_number,
            clientData: updatedData,
          },
          this.getAccessToken
        );

        this.client = response.client;
        this.$store.commit("setCurrentClient", response.client);
        this.showSnackbar("Client information updated successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error updating client info:", error);
        this.showSnackbar("Error updating client information", "error");
        this.$store.commit("setLoader", false);
      }
    },

    refreshClientDetails() {
      if (this.client && this.client.id_number) {
        this.getClientMethod(this.client.id_number);
      }
    },

    // Utility methods
    normalizeNames(name) {
      if (!name) return "";
      const names = name.split(" ");
      if (names.length === 1) return name;

      let normalizedName = names[0];
      for (let i = 1; i < names.length; i++) {
        normalizedName += " " + names[i].charAt(0) + ".";
      }
      return normalizedName;
    },

    formatNumber(value) {
      if (!value && value !== 0) return "0.00";
      return Number(value)
        .toFixed(2)
        .replace(/\d(?=(\d{3})+\.)/g, "$&,");
    },

    formatDate(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString();
    },

    formatDateTime(dateTime) {
      if (!dateTime) return "";
      return new Date(dateTime).toLocaleString();
    },

    showSnackbar(text, color = "success") {
      this.snackbar.text = text;
      this.snackbar.color = color;
      this.snackbar.show = true;
    },

    getStatusColor(status) {
      const statusColors = {
        scheduled: "blue",
        completed: "green",
        cancelled: "red",
        pending: "orange",
        active: "green",
        closed: "grey",
        paid: "green",
        unpaid: "red",
        overdue: "red darken-2",
      };

      return statusColors[status ? status.toLowerCase() : ""] || "grey";
    },

    getAppointmentColor(appointment) {
      if (appointment.status === "cancelled") return "red";

      const aptDate = new Date(appointment.date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (aptDate < today) return "grey";
      if (aptDate.toDateString() === today.toDateString()) return "green";

      return "blue";
    },

    getInvoiceStatusColor(invoice) {
      if (!invoice.calculation) return "grey";

      const total = Number(invoice.calculation.total || 0);
      const paid = Number(invoice.calculation.paid || 0);

      if (paid >= total) return "green";
      if (paid > 0) return "orange";

      // Check if overdue
      if (invoice.due_date) {
        const dueDate = new Date(invoice.due_date);
        const today = new Date();
        if (dueDate < today) return "red darken-2";
      }

      return "red";
    },

    getInvoiceStatusText(invoice) {
      if (!invoice.calculation) return "Unknown";

      const total = Number(invoice.calculation.total || 0);
      const paid = Number(invoice.calculation.paid || 0);

      if (paid >= total) return "Paid";
      if (paid > 0) return "Partially Paid";

      // Check if overdue
      if (invoice.due_date) {
        const dueDate = new Date(invoice.due_date);
        const today = new Date();
        if (dueDate < today) return "Overdue";
      }

      return "Unpaid";
    },

    getMedicalFileIcon(fileType) {
      const icons = {
        consultation: "mdi-stethoscope",
        followup: "mdi-calendar-check",
        procedure: "mdi-hospital-box",
        emergency: "mdi-ambulance",
        checkup: "mdi-clipboard-pulse",
      };

      return icons[fileType] || "mdi-file-document";
    },

    getCaseStatusIcon(status) {
      const icons = {
        active: "mdi-progress-clock",
        pending: "mdi-timer-sand",
        completed: "mdi-check-circle",
        closed: "mdi-close-circle",
        "on hold": "mdi-pause-circle",
      };

      const statusLower = status ? status.toLowerCase() : "";
      return icons[statusLower] || "mdi-file-document";
    },

    getPaymentMethodIcon(type) {
      const icons = {
        cash: "mdi-cash",
        card: "mdi-credit-card",
        insurance: "mdi-shield-check",
        bank_transfer: "mdi-bank-transfer",
        cheque: "mdi-checkbook",
        mobile_money: "mdi-cellphone",
      };

      return icons[type] || "mdi-cash-multiple";
    },

    closeAllDialogs() {
      this.paymentMethodDialog = false;
      this.appointmentDialog = false;
      this.addCaseDialog = false;
      this.addMedicalFileDialog = false;
      this.addVehicleDialog = false;
      this.smsDialog = false;
      this.notesDialog = false;
    },

    // Opening balance update
    async updateOpeningBalance(newAmount) {
      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/update-opening-balance",
          {
            clientId: this.client.id_number,
            amount: newAmount,
          },
          this.getAccessToken
        );

        this.financeTotal[0].amount = response.opening_balance;
        this.showSnackbar("Opening balance updated successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error updating opening balance:", error);
        this.showSnackbar("Error updating opening balance", "error");
        this.$store.commit("setLoader", false);
      }
    },

    // Export to Excel
    async exportToExcel() {
      this.$store.commit("setLoader", true);

      try {
        const response = await axios.postDataWithToken(
          "/auth/export-client-data",
          {
            clientId: this.client.id_number,
            includeInvoices: true,
            includeAppointments: true,
          },
          this.getAccessToken
        );

        // Create a blob and download
        const blob = new Blob([response.data], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `${
          this.client.name
        }-export-${new Date().getTime()}.xlsx`;
        link.click();
        window.URL.revokeObjectURL(url);

        this.showSnackbar("Data exported successfully");
        this.$store.commit("setLoader", false);
      } catch (error) {
        console.error("Error exporting data:", error);
        this.showSnackbar("Error exporting data", "error");
        this.$store.commit("setLoader", false);
      }
    },
  },
};
</script>

<style scoped>
.v-card--reveal {
  align-items: center;
  bottom: 0;
  justify-content: center;
  opacity: 0.9;
  position: absolute;
  width: 100%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.3s;
}

.slide-enter,
.slide-leave-to {
  transform: translateY(-10px);
  opacity: 0;
}

.finance-card {
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);
}

.finance-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.2) !important;
}

.appointment-card {
  transition: all 0.2s ease;
  border-left: 4px solid transparent;
}

.appointment-card:hover {
  border-left-color: var(--v-primary-base);
  background-color: rgba(0, 0, 0, 0.02);
}

.invoice-item {
  transition: background-color 0.2s;
}

.invoice-item:hover {
  background-color: rgba(0, 0, 0, 0.03);
}

.stat-card {
  position: relative;
  overflow: hidden;
}

.stat-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 4px;
  height: 100%;
  background: linear-gradient(
    to bottom,
    var(--v-primary-base),
    var(--v-secondary-base)
  );
}

.entity-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1;
}

.quick-action-btn {
  transition: all 0.2s ease;
}

.quick-action-btn:hover {
  transform: scale(1.05);
}

@media (max-width: 960px) {
  .finance-card {
    margin-bottom: 12px;
  }
}
</style>
